import React, { useCallback } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined'
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined'
import { toast } from 'react-toastify'
import {
  backgroundJobService,
  formatTimeAgo,
  ActiveJobs,
  JobMeta,
} from '../../services/background-job.service'
import Helper from '../../utils/helper'

interface BackgroundJobsDialogProps {
  open: boolean
  onClose: () => void
  activeJobs: ActiveJobs | null
  onRefresh: () => void
}

const GRID_ACTIVE = '24px 1fr 100px 65px'
const GRID_RECENT = '24px 1fr 65px'

export const BackgroundJobsDialog: React.FC<BackgroundJobsDialogProps> = ({
  open,
  onClose,
  activeJobs,
  onRefresh,
}) => {
  const handleCancel = useCallback(
    async (jobKey: string) => {
      try {
        await backgroundJobService.cancel(jobKey)
        onRefresh()
      } catch {
        toast.error('Failed to cancel job.', Helper.bottom_center())
      }
    },
    [onRefresh]
  )

  const running = activeJobs?.running || []
  const queued = activeJobs?.queued || []
  const recent = activeJobs?.recent || []
  const hasActivity = running.length > 0 || queued.length > 0 || recent.length > 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '70vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>Background Jobs</Typography>
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 0, pb: 2 }}>
        {activeJobs?.disabled && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CloudOffOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Background jobs disabled &mdash; Redis server not found
            </Typography>
          </Box>
        )}
        {!activeJobs?.disabled && !hasActivity && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <WorkOffOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No background jobs</Typography>
          </Box>
        )}

        {running.length > 0 && (
          <Section label="Running" count={running.length} color="primary">
            {running.map((job) => (
              <Row key={job.job_key} grid={GRID_ACTIVE}>
                <CircularProgress size={16} />
                <NameCell>
                  {job.display_name}
                  {job.source !== 'beat' && <CancelBtn onClick={() => handleCancel(job.job_key)} />}
                </NameCell>
                <MetaCell>{job.started_by}</MetaCell>
                <TimeCell timestamp={job.started_at || job.queued_at} />
              </Row>
            ))}
          </Section>
        )}

        {queued.length > 0 && (
          <Section label="Queued" count={queued.length} color="text.secondary">
            {queued.map((job, idx) => (
              <Row key={job.job_key} grid={GRID_ACTIVE}>
                <HourglassBottomIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <NameCell>
                  <Box component="span" sx={{ color: 'text.disabled', mr: 0.5 }}>#{idx + 1}</Box>
                  {job.display_name}
                  <CancelBtn onClick={() => handleCancel(job.job_key)} />
                </NameCell>
                <MetaCell>{job.started_by}</MetaCell>
                <TimeCell timestamp={job.queued_at} />
              </Row>
            ))}
          </Section>
        )}

        {recent.length > 0 && (
          <Section label="Recent">
            {recent.map((job) => (
              <Row key={job.job_key} grid={GRID_RECENT} sx={{ opacity: 0.85 }}>
                <StatusIcon status={job.status} />
                <Typography variant="body2" fontWeight={500} noWrap color={statusColor(job.status)}>
                  {job.display_name}
                </Typography>
                <TimeCell timestamp={job.completed_at} />
              </Row>
            ))}
          </Section>
        )}
      </DialogContent>
    </Dialog>
  )
}

/* ---------- Layout ---------- */

const Section: React.FC<{
  label: string; count?: number; color?: string; children: React.ReactNode
}> = ({ label, count, color, children }) => (
  <Box sx={{ mt: 1.5, '&:first-of-type': { mt: 0.5 } }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
      <Typography
        variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}
        color={color || 'text.secondary'}
      >
        {label}
      </Typography>
      {count !== undefined && (
        <Chip label={count} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 600 }} />
      )}
    </Stack>
    <Stack spacing={0.5}>{children}</Stack>
  </Box>
)

const Row: React.FC<{ grid: string; children: React.ReactNode; sx?: object }> = ({ grid, children, sx }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: grid,
      alignItems: 'center',
      gap: 1.5,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      bgcolor: 'action.hover',
      minHeight: 36,
      ...sx,
    }}
  >
    {children}
  </Box>
)

/* ---------- Cells ---------- */

const NameCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
    <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: '1 1 auto', minWidth: 0 }}>
      {children}
    </Typography>
  </Stack>
)

const MetaCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" noWrap>{children}</Typography>
)

const TimeCell: React.FC<{ timestamp: number | null }> = ({ timestamp }) => (
  <Typography variant="caption" color="text.secondary" noWrap sx={{ textAlign: 'right' }}>
    {formatTimeAgo(timestamp)}
  </Typography>
)

/* ---------- Icons ---------- */

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const sx = { fontSize: 16 }
  switch (status) {
    case 'SUCCESS':
      return <Tooltip title="Completed" arrow><CheckCircleIcon sx={{ ...sx, color: 'success.main' }} /></Tooltip>
    case 'FAILURE':
      return <Tooltip title="Failed" arrow><ErrorOutlineIcon sx={{ ...sx, color: 'error.main' }} /></Tooltip>
    case 'REVOKED':
      return <Tooltip title="Cancelled" arrow><CancelOutlinedIcon sx={{ ...sx, color: 'warning.main' }} /></Tooltip>
    default:
      return <Box sx={{ width: 16 }} />
  }
}

const CancelBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    size="small" color="error" onClick={onClick}
    sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, textTransform: 'none', lineHeight: 1.6, flexShrink: 0 }}
  >
    Cancel
  </Button>
)

const statusColor = (status: string) => {
  switch (status) {
    case 'SUCCESS': return 'success.main'
    case 'FAILURE': return 'error.main'
    case 'REVOKED': return 'warning.main'
    default: return 'text.primary'
  }
}
